import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs, { Dayjs } from 'dayjs'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { getAllDepartments } from '@/modules/Employee/services'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useForm, useWatch } from 'react-hook-form'

import { AssetFormData } from '.'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'

import BasicDetailsSection from './sections/BasicDetailsSection'
import DocumentClassificationSection from './sections/DocumentClassificationSection'
import SecurityClassificationSection from './sections/SecurityClassificationSection'
import SharingSection from './sections/SharingSection'
import EffectiveDatesSection from './sections/EffectiveDatesSection'
import NotificationRecipientsSection from './sections/NotificationRecipientsSection'

interface ShareFormValues {
    shareScope: 'public' | 'private'
    shared_employee_ids: number[]
    shared_department_ids: number[]
    startDate: Dayjs | null
    endDate: Dayjs | null
    notifyScope: 'none' | 'all' | 'custom'
    notify_employee_ids: number[]
    notify_department_ids: number[]
}

interface Step1BasicInfoProps {
    formData: AssetFormData
    setFormData: React.Dispatch<React.SetStateAction<AssetFormData>>
}

const Step1BasicInfo: React.FC<Step1BasicInfoProps> = ({
    formData,
    setFormData,
}) => {
    const { t } = useTranslation('asset')
    const { control, setValue } = useForm<ShareFormValues>({
        defaultValues: {
            shareScope: formData.shareScope || 'public',
            shared_employee_ids: formData.shared_employee_ids || [],
            shared_department_ids: formData.shared_department_ids || [],
            startDate: formData.startDate ? dayjs(formData.startDate) : null,
            endDate: formData.endDate ? dayjs(formData.endDate) : null,
            notifyScope: formData.notifyScope || 'none',
            notify_employee_ids: formData.notify_employee_ids || [],
            notify_department_ids: formData.notify_department_ids || [],
        },
    })
    const shareScope = useWatch({ control, name: 'shareScope' })
    const sharedEmployeeIds = useWatch({
        control,
        name: 'shared_employee_ids',
    })
    const sharedDepartmentIds = useWatch({
        control,
        name: 'shared_department_ids',
    })
    const startDate = useWatch({ control, name: 'startDate' })
    const endDate = useWatch({ control, name: 'endDate' })
    const notifyScope = useWatch({ control, name: 'notifyScope' })
    const notifyEmployeeIds = useWatch({
        control,
        name: 'notify_employee_ids',
    })
    const notifyDepartmentIds = useWatch({
        control,
        name: 'notify_department_ids',
    })

    // Fetch departments using React Query
    const { data: departmentsData, isLoading: isDepartmentsLoading } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.DEPARTMENTS],
            queryFn: getAllDepartments,
        })

    useEffect(() => {
        setFormData((prev) => {
            const newScope = shareScope || 'public'
            const newEmpIds = sharedEmployeeIds || []
            const newDeptIds = sharedDepartmentIds || []

            // Check if values actually changed to prevent infinite loop
            const scopeChanged = prev.shareScope !== newScope
            // Arrays of IDs can be compared via JSON.stringify for simplicity here
            const empChanged =
                JSON.stringify(prev.shared_employee_ids) !==
                JSON.stringify(newEmpIds)
            const deptChanged =
                JSON.stringify(prev.shared_department_ids) !==
                JSON.stringify(newDeptIds)

            const formattedStartDate = startDate
                ? startDate.format('YYYY-MM-DD')
                : ''
            const formattedEndDate = endDate ? endDate.format('YYYY-MM-DD') : ''

            const startDateChanged = prev.startDate !== formattedStartDate
            const endDateChanged = prev.endDate !== formattedEndDate

            const newNotifyScope = notifyScope || 'none'
            const newNotifyEmpIds = notifyEmployeeIds || []
            const newNotifyDeptIds = notifyDepartmentIds || []

            const notifyScopeChanged = prev.notifyScope !== newNotifyScope
            const notifyEmpChanged =
                JSON.stringify(prev.notify_employee_ids) !==
                JSON.stringify(newNotifyEmpIds)
            const notifyDeptChanged =
                JSON.stringify(prev.notify_department_ids) !==
                JSON.stringify(newNotifyDeptIds)

            if (
                !scopeChanged &&
                !empChanged &&
                !deptChanged &&
                !startDateChanged &&
                !endDateChanged &&
                !notifyScopeChanged &&
                !notifyEmpChanged &&
                !notifyDeptChanged
            ) {
                return prev
            }

            return {
                ...prev,
                shareScope: newScope,
                shared_employee_ids: newEmpIds,
                shared_department_ids: newDeptIds,
                startDate: formattedStartDate,
                endDate: formattedEndDate,
                notifyScope: newNotifyScope,
                notify_employee_ids: newNotifyEmpIds,
                notify_department_ids: newNotifyDeptIds,
            }
        })
    }, [
        setFormData,
        shareScope,
        sharedEmployeeIds,
        sharedDepartmentIds,
        startDate,
        endDate,
        notifyScope,
        notifyEmployeeIds,
        notifyDepartmentIds,
    ])

    useEffect(() => {
        if (shareScope === 'public') {
            setValue('shared_employee_ids', [])
            setValue('shared_department_ids', [])
        }
    }, [shareScope, setValue])

    useEffect(() => {
        if (notifyScope !== 'custom') {
            setValue('notify_employee_ids', [])
            setValue('notify_department_ids', [])
        }
    }, [notifyScope, setValue])

    return (
        <Card>
            <CardHeader>
                <CardTitle>{t('form.assetInformation')}</CardTitle>
            </CardHeader>
            <CardContent
                className="space-y-6 bg-white"
                data-tour="basic-info-form"
            >
                <BasicDetailsSection
                    formData={formData}
                    setFormData={setFormData}
                    departmentsData={departmentsData}
                    isDepartmentsLoading={isDepartmentsLoading}
                />

                <DocumentClassificationSection
                    formData={formData}
                    setFormData={setFormData}
                />

                <SecurityClassificationSection
                    formData={formData}
                    setFormData={setFormData}
                />

                <EffectiveDatesSection
                    control={control}
                    setValue={setValue}
                    formData={formData}
                />

                <SharingSection
                    control={control}
                    shareScope={shareScope}
                    isRegulation={formData.documentType === 'REGULATION'}
                />

                <NotificationRecipientsSection
                    control={control}
                    setValue={setValue}
                    notifyScope={notifyScope}
                    shareScope={shareScope}
                    sharedEmployeeIds={sharedEmployeeIds}
                    sharedDepartmentIds={sharedDepartmentIds}
                    formData={formData}
                    setFormData={setFormData}
                />
            </CardContent>
        </Card>
    )
}

export default Step1BasicInfo
